import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DashboardFloatingAlertStore } from 'dashboard-components/js/shared/DashboardFloatingAlertStore.ts';
import H5 from 'UIComponents/elements/headings/H5';
import Small from 'UIComponents/elements/Small';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UICheckbox from 'UIComponents/input/UICheckbox';
import { Popover } from 'foundations-components/transitional/components/Popover';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import { View } from 'foundations-components/components/View';
// @ts-expect-error migrate upstream dependency
import { getDashboardReports } from 'dashboard-lib/public/dashboard/dashboard-logic';
// @ts-expect-error migrate upstream dependency
import { getId as getDashboardId } from 'reporting-action-components/data/schemas/dashboardSchema';
// @ts-expect-error migrate upstream dependency
import { useUserRoleForDashboard } from 'reporting-action-components/utils/trackingUtils';
import { generateQuickFilterGroups
// @ts-expect-error migrate upstream dependency
} from '../../../../ducks/quick-filters/quickFilters';
import { QUICK_FILTER_LIMIT, selectPinnedItemCount, selectQuickFilterGroups } from '../../../../ducks/quick-filters';
import { QUICK_FILTER_TEMPLATES, MAX_QUICK_FILTER_TEMPLATES, getExistingGeneratedQuickFilterGroupTemplates, isDateRangeGeneratedQuickFilterGroup, isOwnersGeneratedQuickFilterGroup, isTeamsGeneratedQuickFilterGroup } from '../../../../lib/dashboardFilters/groupQuickFilterHelpers';
import { GenerateQuickFilterConfirmationModal } from './GenerateQuickFilterConfirmationModal';
import { GenerateQuickFilterGroupsPopoverButton } from './GenerateQuickFilterGroupsPopoverButton';

// @ts-expect-error migrate upstream dependency
import { track, EVENTS } from '../../../../../tracking/DashboardUITracker';
import { isSingleObjectReport } from 'reporting-data/tsTypes/reportTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TEMPLATE_TYPE_BY_LABEL = {
  [QUICK_FILTER_TEMPLATES.DATE_RANGE]: () => I18n.text('dashboardHeader.dateRange.label'),
  [QUICK_FILTER_TEMPLATES.OWNER]: () => I18n.text('dashboardHeader.ownerContext.label'),
  [QUICK_FILTER_TEMPLATES.TEAM]: () => I18n.text('dashboardHeader.teamContext.label')
};
export const GenerateQuickFilterGroupsPopover = ({
  dashboard
}) => {
  const [open, setOpen] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [generatedQuickFilterGroups, setGeneratedQuickFilterGroups] = useState([]);
  const quickFilterGroups = useSelector(selectQuickFilterGroups);
  const existingGeneratedQuickFilterGroupTemplates = new Set(getExistingGeneratedQuickFilterGroupTemplates(quickFilterGroups));
  const [quickFilterGroupGenerationOptions, setQuickFilterGroupGenerationOptions] = useState(existingGeneratedQuickFilterGroupTemplates);
  const dispatch = useDispatch();
  const numQuickFilters = useSelector(selectPinnedItemCount);
  const userRole = useUserRoleForDashboard(dashboard);
  const dashboardId = getDashboardId(dashboard);
  const getNewQuickFilterGroupTemplateOptions = () => {
    const newQuickFilterGroupTemplateOptions = new Set(quickFilterGroupGenerationOptions);
    for (const existingGeneratedQuickFilterGroupTemplate of existingGeneratedQuickFilterGroupTemplates) {
      if (newQuickFilterGroupTemplateOptions.has(existingGeneratedQuickFilterGroupTemplate)) {
        newQuickFilterGroupTemplateOptions.delete(existingGeneratedQuickFilterGroupTemplate);
      }
    }
    return newQuickFilterGroupTemplateOptions;
  };
  const willExceedQuickFilterLimit = numQuickFilters + getNewQuickFilterGroupTemplateOptions().size > QUICK_FILTER_LIMIT;
  const hasNotSelectedGenerationOptions = getNewQuickFilterGroupTemplateOptions().size === 0;
  const hasMaxExistingQuickFilterTemplates = existingGeneratedQuickFilterGroupTemplates.size === MAX_QUICK_FILTER_TEMPLATES;
  const getAddPinsDisableMessage = () => {
    if (hasMaxExistingQuickFilterTemplates) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quickFilter.groupedQuickFilter.generation.hasMaxQuickFilterTemplates"
      });
    } else if (willExceedQuickFilterLimit) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quickFilter.groupedQuickFilter.generation.willExceedQuickFilterLimitDisableMessage"
      });
    } else if (hasNotSelectedGenerationOptions) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "quickFilter.groupedQuickFilter.generation.hasNotSelectedGenerationOptions"
      });
    }
    return undefined;
  };
  const isQuickFilterTemplateOptionDisabled = quickFilterTemplate => {
    return existingGeneratedQuickFilterGroupTemplates.has(quickFilterTemplate);
  };
  const handleTogglePopover = () => {
    // If the popover is closing, reset the selected quick filter templates.
    if (open) {
      setQuickFilterGroupGenerationOptions(new Set());
    } else {
      setQuickFilterGroupGenerationOptions(existingGeneratedQuickFilterGroupTemplates);
      track(EVENTS.openGenerateQuickFilterGroupsPopover, {
        actionType: 'explore',
        assetType: 'dashboard',
        action: 'open-generate-quick-filters',
        userRole
      });
    }
    setOpen(!open);
    setGeneratedQuickFilterGroups([]);
  };
  const handleQuickFilterGroupGenerationOptionSelect = e => {
    const {
      target: {
        value
      }
    } = e;
    setQuickFilterGroupGenerationOptions(nextOptions => {
      if (nextOptions.has(value)) {
        nextOptions.delete(value);
      } else {
        nextOptions.add(value);
      }
      return new Set(nextOptions);
    });
  };
  const handleClickAddGeneratedQuickFilterGroups = () => {
    const quickFilterGroupGenerationOptionsList = [...getNewQuickFilterGroupTemplateOptions()];
    track(EVENTS.clickGenerateQuickFilterGroups, {
      actionType: 'explore',
      action: 'generate-quick-filters',
      assetType: 'dashboard',
      role: userRole,
      interactionSource: 'dashboard',
      quickFilterTemplates: quickFilterGroupGenerationOptionsList
    });
    dispatch(generateQuickFilterGroups({
      dashboardId,
      quickFilterGroupGenerationOptionsList
    })).then(({
      duplicatesExists,
      generatedQuickFilterGroups: generatedFromApi
    }) => {
      if (duplicatesExists) {
        setShowConfirmationModal(true);
        setGeneratedQuickFilterGroups(generatedFromApi);
      }
      if (generatedFromApi.length < quickFilterGroupGenerationOptionsList.length) {
        const quickFilterGroupTemplateOptionsThatFailedGeneration = quickFilterGroupGenerationOptionsList.filter(templateType => {
          if (templateType === QUICK_FILTER_TEMPLATES.DATE_RANGE) {
            return !generatedFromApi.some(isDateRangeGeneratedQuickFilterGroup);
          }
          if (templateType === QUICK_FILTER_TEMPLATES.OWNER) {
            return !generatedFromApi.some(isOwnersGeneratedQuickFilterGroup);
          }
          return !generatedFromApi.some(isTeamsGeneratedQuickFilterGroup);
        });
        DashboardFloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: quickFilterGroupTemplateOptionsThatFailedGeneration.length === 1 ? 'quickFilter.dynamicQuickFilterNotEnoughPropertiesError.single' : 'quickFilter.dynamicQuickFilterNotEnoughPropertiesError.plural',
            options: {
              template: quickFilterGroupTemplateOptionsThatFailedGeneration.map(templateType => TEMPLATE_TYPE_BY_LABEL[templateType]()).join(', ')
            }
          }),
          type: 'danger'
        });
      }
      setOpen(false);
    }).catch(() => {
      DashboardFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quickFilter.fetchFailed",
          options: {
            action: 'generate'
          }
        }),
        type: 'danger'
      });
    });
  };
  const reports = getDashboardReports(dashboard);
  const canApplyGeneratedQuickFilterGroups = reports.filter(isSingleObjectReport).length > 0;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Popover, {
      open: open,
      onOpenChange: handleTogglePopover,
      placement: "bottom",
      inlineSize: "300px",
      "data-test-id": "generate-quick-filter-group-popover",
      content: {
        // TODO: refactor this to a separate component, that we can test independently
        body: /*#__PURE__*/_jsx(View, {
          paddingBlockEnd: "500",
          children: /*#__PURE__*/_jsx(UIGrid, {
            children: /*#__PURE__*/_jsxs(UIGridItem, {
              size: 12,
              children: [/*#__PURE__*/_jsxs(H5, {
                children: [/*#__PURE__*/_jsx(UIIcon, {
                  name: "dynamicFilter"
                }), ' ', /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "quickFilter.groupedQuickFilter.generation.popoverTitle"
                })]
              }), /*#__PURE__*/_jsxs(Small, {
                children: [/*#__PURE__*/_jsx(FormattedMessage, {
                  message: "quickFilter.groupedQuickFilter.generation.description"
                }), ' ', /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(UIButton, {
                  external: true,
                  use: "link",
                  href: "https://knowledge.hubspot.com/dashboards/use-dashboard-filters",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "dashboardHeader.dashboardFilters.allFilters.panel.learnMoreLink"
                  })
                })]
              }), /*#__PURE__*/_jsx(UITooltip, {
                title: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "quickFilter.groupedQuickFilter.generation.quickFilterTemplateDisableMessage"
                }),
                placement: "left top",
                disabled: canApplyGeneratedQuickFilterGroups,
                children: /*#__PURE__*/_jsxs(UIToggleGroup, {
                  name: "quick-filter-templates",
                  className: "m-bottom-2",
                  children: [/*#__PURE__*/_jsx(UICheckbox, {
                    checked: quickFilterGroupGenerationOptions.has(QUICK_FILTER_TEMPLATES.DATE_RANGE),
                    disabled: !canApplyGeneratedQuickFilterGroups,
                    value: QUICK_FILTER_TEMPLATES.DATE_RANGE,
                    onChange: handleQuickFilterGroupGenerationOptionSelect,
                    readOnly: isQuickFilterTemplateOptionDisabled(QUICK_FILTER_TEMPLATES.DATE_RANGE),
                    "data-test-id": "generated-quick-filter-group_date-range",
                    children: TEMPLATE_TYPE_BY_LABEL[QUICK_FILTER_TEMPLATES.DATE_RANGE]()
                  }), /*#__PURE__*/_jsx(UICheckbox, {
                    checked: quickFilterGroupGenerationOptions.has(QUICK_FILTER_TEMPLATES.OWNER),
                    disabled: !canApplyGeneratedQuickFilterGroups,
                    value: QUICK_FILTER_TEMPLATES.OWNER,
                    onChange: handleQuickFilterGroupGenerationOptionSelect,
                    readOnly: isQuickFilterTemplateOptionDisabled(QUICK_FILTER_TEMPLATES.OWNER),
                    "data-test-id": "generated-quick-filter-group_owner",
                    children: TEMPLATE_TYPE_BY_LABEL[QUICK_FILTER_TEMPLATES.OWNER]()
                  }), /*#__PURE__*/_jsx(UICheckbox, {
                    checked: quickFilterGroupGenerationOptions.has(QUICK_FILTER_TEMPLATES.TEAM),
                    disabled: !canApplyGeneratedQuickFilterGroups,
                    value: QUICK_FILTER_TEMPLATES.TEAM,
                    onChange: handleQuickFilterGroupGenerationOptionSelect,
                    readOnly: isQuickFilterTemplateOptionDisabled(QUICK_FILTER_TEMPLATES.TEAM),
                    "data-test-id": "generated-quick-filter-group_teams",
                    children: TEMPLATE_TYPE_BY_LABEL[QUICK_FILTER_TEMPLATES.TEAM]()
                  })]
                })
              }), /*#__PURE__*/_jsx(UITooltip, {
                disabled: !(willExceedQuickFilterLimit || hasNotSelectedGenerationOptions),
                title: getAddPinsDisableMessage(),
                children: /*#__PURE__*/_jsx(UIButton, {
                  use: "tertiary",
                  size: 'small',
                  onClick: handleClickAddGeneratedQuickFilterGroups,
                  disabled: willExceedQuickFilterLimit || hasNotSelectedGenerationOptions,
                  "data-test-id": "generated-quick-filter-group_add",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "quickFilter.groupedQuickFilter.generation.popoverAddPinButton"
                  })
                })
              })]
            })
          })
        })
      },
      children: /*#__PURE__*/_jsx(GenerateQuickFilterGroupsPopoverButton, {
        dashboard: dashboard,
        handleTogglePopover: handleTogglePopover
      })
    }), showConfirmationModal && /*#__PURE__*/_jsx(GenerateQuickFilterConfirmationModal, {
      handleClose: () => {
        setShowConfirmationModal(false);
        setGeneratedQuickFilterGroups([]);
      },
      generatedQuickFilterGroups: generatedQuickFilterGroups
    })]
  });
};